import { css } from '@emotion/react';
import { ActionChip, Heading, Paragraph, semanticTokens, tokens } from '@flysas-tech/commercial-ui';
import { t } from 'i18n';
import { Airport, BOUND_SIDE } from 'shared';

import { getOriginAndDestinationLabel } from '../../utils';
import { DisplayId } from '../modals';

import { SegementDetails } from './types';

const styles = {
  infoWrapper: css`
    display: flex;
    gap: ${tokens.dimension['space-5']};
    flex-direction: column;
  `,
  heading: css`
    display: flex;
    flex-direction: column;
    gap: ${tokens.dimension['space-2']};
  `,
  bound: css`
    color: ${semanticTokens.color.text.secondary};
    font-size: ${tokens.fontSize.paragraph['paragraph-2']};
  `,
  detailDivider: css`
    margin: 0 ${tokens.dimension['space-3']};
    color: ${semanticTokens.color.border.subtle};

    &:after {
      content: '|';
    }
  `,
  buttonContainer: css`
    display: flex;
    gap: ${tokens.dimension['space-5']};
  `,
};

type Props = {
  boundSide: BOUND_SIDE;
  journeyDetails: SegementDetails;
  productName: string;
  paddingSmall?: boolean;
  findAirportByCode?: (code: string) => Airport | undefined;
  onClickHandler: (displayId: DisplayId) => () => void;
};

export const FlightSummary = ({ boundSide, journeyDetails, onClickHandler, productName, findAirportByCode }: Props) => {
  const {
    originAirport,
    arrivalAirport,
    originDate,
    arrivalDateTimeLocal,
    originDateTimeLocal,
    stops,
    flightDuration,
  } = journeyDetails;

  return (
    <div css={styles.infoWrapper}>
      <Heading as="h3" size="title-3" color="primary" css={styles.heading}>
        <span css={styles.bound}>
          {boundSide === BOUND_SIDE.Outbound ? `${t('common:outBound')}` : `${t('common:inBound')}`}
        </span>
        <span>
          {findAirportByCode &&
            getOriginAndDestinationLabel(findAirportByCode(originAirport.code), findAirportByCode(arrivalAirport.code))}
        </span>
      </Heading>

      <Paragraph size="paragraph-3" color="primary">
        {originDate}
        <span css={styles.detailDivider} />
        {`${originDateTimeLocal} - ${arrivalDateTimeLocal}`}
        <span css={styles.detailDivider} />
        {`${t('formattedTime', { hours: flightDuration.hours, minutes: flightDuration.minutes })}`}
        <span css={styles.detailDivider} />
        {stops}
      </Paragraph>

      <div css={styles.buttonContainer}>
        <ActionChip
          icon={'InfoCircle'}
          iconPlace={'left'}
          size={'large'}
          label={t('common:flightDetails')}
          onClick={() =>
            onClickHandler(
              boundSide === BOUND_SIDE.Outbound ? DisplayId.OutboundJourneyDetails : DisplayId.InboundJourneyDetails
            )()
          }
        />
        <ActionChip
          icon={'Trips'}
          iconPlace={'left'}
          size={'large'}
          label={productName}
          onClick={() =>
            onClickHandler(boundSide === BOUND_SIDE.Outbound ? DisplayId.OutboundFare : DisplayId.InboundFare)()
          }
        />
      </div>
    </div>
  );
};
